import React, { useState, useEffect } from 'react';
import { Bell, X, MessageCircle } from 'lucide-react';
import { SiteSettings } from '../types';

interface WhatsappFloatingButtonProps {
  settings: SiteSettings;
  apiUrl?: string;
}

interface PushNotification {
  id: string;
  title: string;
  message: string;
  sent_at: string;
}

const WhatsappFloatingButton: React.FC<WhatsappFloatingButtonProps> = ({ settings, apiUrl }) => {
  const [latestNotif, setLatestNotif] = useState<PushNotification | null>(null);
  const [hasUnread, setHasUnread] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!apiUrl) return;

    const checkLatestNotification = async () => {
      try {
        const response = await fetch(`${apiUrl}?action=get_latest_notification`);
        if (response.ok) {
          const data: PushNotification | null = await response.json();
          if (data && data.id) {
            setLatestNotif(data);
            const lastViewedId = localStorage.getItem('last_viewed_notification_id');
            if (lastViewedId !== data.id) {
              setHasUnread(true);
            } else {
              setHasUnread(false);
            }
          } else {
            setLatestNotif(null);
            setHasUnread(false);
          }
        }
      } catch (err) {
        console.warn('Unable to retrieve latest notification (polling):', err);
      }
    };

    // Check immediately on mount
    checkLatestNotification();

    // Poll every 10 seconds for real-time notification alerts
    const interval = setInterval(checkLatestNotification, 10000);
    return () => clearInterval(interval);
  }, [apiUrl]);

  if (!settings.whatsappNumber) return null;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const message = encodeURIComponent(`Olá! Estou no site ${currentUrl} e gostaria de mais informações.`);
  const whatsappUrl = `https://wa.me/${settings.whatsappNumber.replace(/\D/g, '')}?text=${message}`;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (hasUnread && latestNotif) {
      e.preventDefault();
      setShowModal(true);
    }
  };

  const handleMarkAsRead = () => {
    if (latestNotif) {
      localStorage.setItem('last_viewed_notification_id', latestNotif.id);
      setHasUnread(false);
    }
    setShowModal(false);
  };

  const handleWhatsAppRedirect = () => {
    if (latestNotif) {
      localStorage.setItem('last_viewed_notification_id', latestNotif.id);
      setHasUnread(false);
    }
    setShowModal(false);
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <a
        href={whatsappUrl}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden md:flex fixed bottom-20 right-6 lg:bottom-8 lg:right-8 z-[140] w-14 h-14 md:w-16 md:h-16 transition-all transform hover:scale-110 active:scale-95 drop-shadow-lg group items-center justify-center rounded-full"
        aria-label="Contato via WhatsApp"
      >
        <svg 
          viewBox="0 0 32 32" 
          className="w-full h-full drop-shadow-md" 
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* White outer border/shape - thinner now */}
          <path 
            d="M16 0C7.163 0 0 7.163 0 16c0 2.825.733 5.479 2.014 7.787L0 32l8.442-2.014A15.91 15.91 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0z" 
            fill="#fff" 
          />
          {/* Green inner shape (creates the thin white border effect) */}
          <path 
            d="M16 0.6C7.495 0.6 0.6 7.495 0.6 16c0 2.707.7 5.247 1.928 7.452L0.4 30.8l7.716-1.842A15.2 15.2 0 0 0 16 31.4c8.505 0 15.4-6.895 15.4-15.4S24.505 0.6 16 0.6z" 
            fill="#25D366" 
          />
          {/* White phone icon */}
          <path 
            d="M21.507 18.533c-.3-.15-1.773-.875-2.047-.975-.275-.1-.475-.15-.675.15s-.775.975-.95 1.175-.35.225-.65.075c-.3-.15-1.267-.467-2.413-1.488-.892-.796-1.494-1.779-1.669-2.079-.175-.3-.019-.463.131-.612.135-.134.3-.35.45-.525.15-.175.2-.3.3-.5s.05-.375-.025-.525-.675-1.625-.925-2.225c-.244-.584-.491-.505-.675-.514-.175-.008-.375-.01-.575-.01s-.525.075-.8.375c-.275.3-1.05 1.025-1.05 2.5s1.075 2.9 1.225 3.1c.15.2 2.113 3.227 5.119 4.525.715.309 1.273.494 1.708.632.718.229 1.371.196 1.887.119.575-.086 1.773-.725 2.023-1.425.25-.7.25-1.3.175-1.425-.075-.125-.275-.2-.575-.35z" 
            fill="#fff" 
          />
        </svg>

        {/* Pulsating Notification Dot on Top Right */}
        {hasUnread && (
          <div className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 border-2 border-white items-center justify-center shadow-md">
              <span className="block w-2 h-2 rounded-full bg-white"></span>
            </span>
          </div>
        )}
      </a>

      {/* Elegant Notification View Modal */}
      {showModal && latestNotif && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-6 animate-scale-up">
            {/* Close button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-all rounded-full bg-slate-50 dark:bg-slate-800"
            >
              <X size={18} />
            </button>

            {/* Icon & Heading */}
            <div className="flex flex-col items-center text-center mt-2 mb-6">
              <div className="w-14 h-14 bg-red-500/10 text-red-500 rounded-2xl flex items-center justify-center mb-4 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-2xl bg-red-400 opacity-20"></span>
                <Bell size={28} className="animate-bounce" />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                Nova Notificação
              </h3>
              <p className="text-xs text-slate-400 font-medium mt-1">
                Enviada pelo Administrador do Guia
              </p>
            </div>

            {/* Notification Content Box */}
            <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800/80 rounded-2xl p-4 mb-6">
              <h4 className="font-bold text-slate-800 dark:text-slate-200 text-base mb-1.5 leading-snug">
                {latestNotif.title}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                {latestNotif.message}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2.5">
              <button
                onClick={handleWhatsAppRedirect}
                className="w-full bg-[#25D366] hover:bg-[#20ba56] text-white py-3.5 px-4 rounded-2xl font-black uppercase tracking-wider text-xs shadow-lg shadow-[#25D366]/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <MessageCircle size={16} /> Contatar no WhatsApp
              </button>
              <button
                onClick={handleMarkAsRead}
                className="w-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 py-3 px-4 rounded-2xl font-bold uppercase tracking-wider text-[11px] active:scale-[0.98] transition-all cursor-pointer text-center"
              >
                Fechar e Marcar como Lida
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsappFloatingButton;
